(ns hai.admin-ui.api-keys
  "Humble API key management interface

  Create/delete Humble API keys so you can build integrations against Humble"
  (:require
   [clojure.string :as str]
   [hai.ui.components :as c]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.input :as input]
   [hai.ui.components.mahdi-table :as mt]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [humbleai.belt.csv :as csv]
   [humbleai.belt.download :as download]
   [lambdaisland.deja-fu :as fu]
   [lambdaisland.ornament :as o]
   [reagent.core :as reagent]))

(def date-format "dd MMM, Y")

(o/defstyled key-or-scope :pre
  [:code :px-2 :py-1 :rounded
   {:color c/--gray-12 :background-color c/--gray-4}]
  ([scope]
   [:code scope]))

(o/defstyled api-keys-ui :div
  [:li :p-1]
  [mt/remote-data-table :mb-7]
  [:.controls
   :flex :flex-row :pb-3
   [:button {:margin-left "auto"}]
   [:label :flex :flex-row :gap-2 :items-center]]
  ["tr:has(.revoked) td" {:text-decoration "line-through"
                          :color c/--gray-9}]
  [:svg [:path {:fill c/--gray-12}]]
  [:.csv-export-button {:display "none"}]
  ([]
   (reagent/with-let [_ (>e [:fetch {:url "/api-key/v1" :request-id ::list}])]
     [:<>
      [:div.controls
       [c/Button {:on-click #(>e [:routing/navigate :connections/new-api-key])}"+ Create API Key"]]
      [mt/remote-data-table ::list
       {:header "Created"
        :render (comp #(fu/format % date-format) :api-key/created-at)}
       {:header "Name"
        :render :api-key/name}
       {:header "Key"
        :render (fn [{:api-key/keys [key revoked]}]
                  [:span {:class (if revoked "revoked" "")}
                   [key-or-scope key]])}
       {:header "Scopes"
        :render (fn [{:api-key/keys [scopes]}]
                  (if (seq scopes)
                    (into [:ul]
                          (map (fn [s] [:li [key-or-scope s]])
                               (str/split scopes #"\s+")))
                    "—"))}
       {:header "Last used"
        :render (fn [{:api-key/keys [last-use]}]
                  (if last-use
                    [:span {:title (fu/format last-use)}
                     (str (fu/distance-in-words last-use (fu/local-date-time))
                          " ago")]
                    "—"))}

       {:header "Expires"
        :render (fn [{:api-key/keys [expires-at]}]
                  (if expires-at
                    [:span {:title (fu/format expires-at)}
                     (str "in "
                          (fu/distance-in-words (fu/local-date-time) expires-at))]
                    "—"))}
       {:header ""
        :render (fn [{:api-key/keys [uuid revoked]}]
                  (when-not revoked
                    [c/DropdownMenuRoot
                     [c/DropdownMenuTrigger
                      [:button
                       [icons/filled-more_vert]]]
                     [c/DropdownMenuContent
                      [c/DropdownMenuItem {:on-click #(>e [:fetch {:method :post
                                                                   :url (str "/api-key/v1/" uuid "/revoke")
                                                                   :on-success-fx [[:dispatch [:fetch/reload ::list]]]}])}
                       "Delete API Key"]]]))}]
      [:div.controls
       [:label
        [c/Checkbox {:on-checked-change (fn [v]
                                          (>e [:fetch {:url (if v
                                                              "/api-key/v1?include_revoked=true"
                                                              "/api-key/v1")
                                                       :request-id ::list}]))}]
        [:div
         "Also show keys that have been revoked or expired"]]]])))


(defn all-scopes [admin?]
  (cond-> [["admin" "Matches all scopes, including scopes we introduce in the future. Use with care."]
           ["assistant:read" "Read access to Assistant knowledge bases and conversations"]
           ["assistant:write" "Create, alter, delete knowledge bases, and post messages to chats"]]
    admin?
    (conj ["superuser" "For internal use, protects specific routes that service internally use"])))

(o/defstyled scopes-ui :div
  :flex :flex-col :gap-3
  [:label :flex :flex-row :gap-2]
  ([{:form.field/keys [set-value! current-value]}]
   (let [scopes (into #{} (remove #{""}) (str/split current-value #"\s+"))]
     (into [:<>]
           (for [[scope desc] (all-scopes (<s :user/admin?))]
             [:label
              [c/Checkbox {:value (boolean (contains? scopes scope))
                           :disabled (and (not= "admin" scope) (contains? scopes "admin"))
                           :on-checked-change (fn [v]
                                                (if v
                                                  (set-value! (str/join " " (conj scopes scope)))
                                                  (set-value! (str/join " " (disj scopes scope)))))}]
              [:div
               [key-or-scope scope]
               [:p desc]]])))))

(def load-time (fu/local-date-time))

(o/defstyled expires-ui :div
  ([{:form.field/keys [set-value! current-value]}]
   [:<>
    [c/SelectRoot
     {:value (some-> current-value fu/format)
      :on-value-change (fn [v]
                         (set-value! (fu/parse-local-date-time v)))}
     [c/SelectTrigger {:placeholder "Expiration time"}]
     [c/SelectContent
      [c/SelectItem {:value (fu/format (fu/add-interval load-time {:months 1}))} "1 month from now"]
      [c/SelectItem {:value (fu/format (fu/add-interval load-time {:months 3}))} "3 months from now"]
      [c/SelectItem {:value (fu/format (fu/add-interval load-time {:months 6}))} "6 months from now"]
      [c/SelectItem {:value (fu/format (fu/add-interval load-time {:years 1}))} "1 year from now"]
      [c/SelectItem {:value (fu/format (fu/add-interval load-time {:years 2}))} "2 years from now"]
      [c/SelectItem {:value (fu/format (fu/add-interval load-time {:years 5}))} "5 years from now"]
      [c/SelectItem {:value nil} "Never expires"]]]]))

(o/defstyled render-form :div
  :flex :flex-col :gap-3
  [:.buttons :flex :flex-row :gap-3]
  ([{:form/keys [fields valid? on-submit submitting?] :as form-props}]
   (let [{:api-key/keys [name scopes expires-at]} fields]
     [:<>
      [:div
       [form/label name]
       [input/field name]]
      [:div
       [form/label scopes]
       [scopes-ui scopes]]
      [:div
       [form/label expires-at]
       [expires-ui expires-at]]
      [:div.buttons
       [c/Button
        {:disabled (or (not valid?)
                       submitting?)
         :on-click on-submit
         :size "2"}
        "Create"]
       [c/Button
        {:disabled submitting?
         :on-click #(>e [:routing/navigate :connections/table])
         :size "2"
         :color "crimson"
         :variant "outline"}
        "Cancel"]]])))

(def KeySchema
  [:map
   [:api-key/name {:title "Description"} :string]
   [:api-key/scopes {:title "Scopes"} :string]
   [:api-key/expires-at {:title "Expires" :optional true} any?]
   ])

(o/defstyled new-api-key-ui :div
  ([]
   [:<>
    [c/Heading "Create new API key"]
    [form/component
     {:id ::form
      :render render-form
      :initial-value {}
      :schema KeySchema
      :on-submit (fn [{:api-key/keys [name scopes expires-at]}]
                   (>e [:fetch {:request-id ::new-api-key
                                :method :post
                                :url "/api-key/v1"
                                :body {:name name :scopes scopes :expires-at (when expires-at (js/Date. expires-at))}
                                :on-success-fx [[:dispatch [:form/submitted ::form]]
                                                [:dispatch [:routing/navigate :connections/show-new-key]]]}]))}]]))

(defn download-key-csv! [{:api-key/keys [uuid name key expires-at scopes]}]
  (download/download!
   (str "humble_key_" uuid ".csv")
   (csv/generate-str
    [["Key ID" "Description" "Key" "Scopes" "Expires"]
     [uuid name key scopes (when expires-at (.toISOString expires-at))]]
    {})
   "text/csv"))

(o/defstyled show-new-key-ui :div
  [:h1 :mb-4]
  [:p :mb-3]
  [#{:pre :code} :inline-block]
  [#{:.buttons :.scopes :span} :flex :flex-row :gap-2]
  ([]
   (reagent/with-let [response (<s [:fetch/request ::new-api-key])]
     (let [{:keys [state response err]} @response]
       (if (= :success state)
         [:<>
          [c/Heading "Your API key"]
          [:p "You can download your API key now, or copy it to the clipboard.
        Save it in a safe place, as the key will not be shown again."]
          (let [{:api-key/keys [name key expires-at scopes]} (:body response)]
            [:<>
             [:p [:strong "Description: "]  name]
             [:p [:strong "Expires: "] (if expires-at
                                         (some-> expires-at .getTime fu/local-date-time (fu/format date-format))
                                         "Never")]
             [:p.scopes [:strong "Scopes: "] (into [:span] (map #(do [key-or-scope %])) (str/split scopes #"\s+"))]
             [:p [:strong "Key: "] [key-or-scope key]]
             [:div.buttons
              [c/Button {:on-click #(download-key-csv! (:body response))} "Download CSV"]
              [c/Button {:on-click #(js/navigator.clipboard.writeText key)} "Copy to Clipboard"]
              [c/Button {:on-click #(>e [:routing/navigate :connections/table])
                         :variant "outline"} "Finish"]]])]
         [:<>
          [c/Heading "Something went wrong"]
          [c/Button {:on-click #(>e [:routing/navigate :connections/table])} "Finish"]])))))
